import { collection, doc, getDoc, getDocs, query, where, setDoc, serverTimestamp } from 'firebase/firestore';
import { definedOnly } from './definedOnly';
import { publicProfileUpdates } from './publicProfileFields';
import { db } from './config';
import { trackPagePublished } from '../utils/analytics';

export interface MissionPage {
  id: string;
  userId: string;
  headerImage: string;
  title: string;
  description: string;
  donationImpact?: string;
  // TSF-238: users/{uid} is `allow get: if isSignedIn()`, so a logged-out
  // donor can never read the creator's profile photo from there. Mirrored
  // here (publicly readable) whenever the creator uploads one, same pattern
  // as headerImage/description below.
  photoURL?: string;
  isPublished: boolean;
  // TSF-176: a page can now be published before Stripe onboarding, so a live
  // page may belong to someone with nowhere to send the money. users/{uid} is
  // `allow get: if isSignedIn()`, so a logged-out donor cannot read the
  // creator's Stripe fields -- this mirrors them onto the publicly readable
  // missionpages doc so the donate form can be honest before the donor types
  // an amount. Absent (every page published before this shipped) means "assume
  // giving works": never hide a donate button we are not sure is dead.
  givingEnabled?: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface Mission {
  id: string;
  slug: string;
  title: string;
  headerImage: string;
  description: string;
  isPublished: boolean;
  userId: string;
  createdAt: any;
  updatedAt: any;
}

/**
 * Content fields a partial save may touch.
 *
 * CC-264 — `isPublished` is deliberately NOT one of them, and the type is what
 * enforces it. Priscilla Alferes reported her athlete's page "shows as not
 * published on every sign-in". definedOnly() already protected a live page
 * from a caller that knew nothing, but two call sites defeated it by writing
 * `isPublished: missionPage?.isPublished || false` — so the moment the page
 * document had not loaded yet, changing a cover photo wrote an explicit
 * `false` over a live page. `|| false` turns "I do not know" into "no", which
 * is exactly the coercion definedOnly exists to prevent, and no amount of
 * fixing it in one caller stops the next one.
 *
 * Publication has its own function, publishMissionPage(uid, boolean), which is
 * a merge write of that one field and nothing else. Leaving isPublished out of
 * this type means a content save CANNOT unpublish a page, by construction, and
 * tsc rejects any future caller that tries.
 */
export type MissionPageContent = Partial<
  Omit<MissionPage, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'isPublished'>
>;

export async function saveMissionPage(userId: string, data: MissionPageContent) {
  try {
    console.log('[saveMissionPage] Saving fund page for userId:', userId);
    console.log('[saveMissionPage] Data to save:', data);
    const pageRef = doc(db, 'missionpages', userId);

    // Get existing document to preserve timestamps
    const existingDoc = await getDoc(pageRef);
    const existingData = existingDoc.exists() ? existingDoc.data() : {};

    // Only write what the caller actually supplied. This used to take every
    // field unconditionally, so a caller that passed `missionPage?.headerImage
    // || ''` while missionPage was still null (see useMissionPage's failed load)
    // wiped the cover, the story AND isPublished off a LIVE page -- with a
    // setDoc that does not merge. That is what a creator experienced as "my
    // photos don't show and it keeps saying my page isn't published".
    //
    // isPublished is absent on purpose — see MissionPageContent. A content save
    // can no longer change publication state at all.
    const docData = {
      ...existingData,
      userId,
      ...definedOnly({
        headerImage: data.headerImage,
        title: data.title,
        description: data.description,
        donationImpact: data.donationImpact,
      }),
      updatedAt: serverTimestamp(),
      createdAt: existingData.createdAt || serverTimestamp(),
    };

    console.log('[saveMissionPage] Final missionpages document data:', docData);
    await setDoc(pageRef, docData);
    console.log('[saveMissionPage] Successfully saved fund page');
    return userId;
  } catch (error: any) {
    console.error('Error saving fund page:', error);
    throw new Error(error.message || 'Failed to save fund page');
  }
}

function missionPageFromSnapshot(pageDoc: { id: string; data: () => any }): MissionPage {
  const data = pageDoc.data();
  return {
    id: pageDoc.id,
    userId: pageDoc.id,
    headerImage: data.headerImage || '',
    title: data.title || '',
    description: data.description || '',
    donationImpact: data.donationImpact || '',
    photoURL: data.photoURL || '',
    isPublished: data.isPublished || false,
    givingEnabled: data.givingEnabled,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt
  } as MissionPage;
}

export async function getMissionPage(userId: string): Promise<MissionPage | null> {
  try {
    console.log('[getMissionPage] Fetching fund page for userId:', userId);
    const pageDoc = await getDoc(doc(db, 'missionpages', userId));
    console.log('[getMissionPage] Document exists:', pageDoc.exists());
    if (!pageDoc.exists()) return null;
    console.log('[getMissionPage] Document data:', pageDoc.data());
    return missionPageFromSnapshot(pageDoc);
  } catch (error: any) {
    console.error('Error fetching fund page:', error);
    throw new Error(error.message || 'Failed to fetch fund page');
  }
}

/**
 * Fast path for the public fund page (ported from mystudyfund): when the URL
 * identifier is already a Firebase UID, resolveUserIdFromIdentifier()
 * (lib/firebase/user.ts) fetches missionpages/{uid} once just to confirm it
 * exists, then discards the data -- MissionPage.tsx used to fetch the exact
 * same document a second time via getMissionPage() right after. Fetches it
 * once and hands the mapped doc back so the page can skip the duplicate
 * round trip. Returns null for a slug identifier or a UID with no page, so
 * callers fall back to the existing resolve+fetch path unchanged.
 */
export async function getMissionPageIfUid(identifier: string): Promise<{ uid: string; missionPage: MissionPage } | null> {
  if (!/^[A-Za-z0-9_-]{20,}$/.test(identifier)) return null;
  try {
    const pageDoc = await getDoc(doc(db, 'missionpages', identifier));
    if (!pageDoc.exists()) return null;
    return { uid: identifier, missionPage: missionPageFromSnapshot(pageDoc) };
  } catch {
    return null;
  }
}

/**
 * TSF-238 again, from the other end. The backfill fixed the pages that already
 * existed; without this, every NEW creator recreated the bug the moment they
 * published, because publishMissionPage wrote isPublished and nothing else --
 * so a freshly published page had no title, no photo and no story on the
 * publicly readable doc, and a logged-out donor saw a grey silhouette.
 * Measured 2026-09-25 by publishing as a brand-new creator: title undefined.
 *
 * Best-effort: a failure here must never take down a publish.
 */
async function mirrorPublicProfile(userId: string) {
  try {
    const [pageSnap, userSnap] = await Promise.all([
      getDoc(doc(db, 'missionpages', userId)),
      getDoc(doc(db, 'users', userId)),
    ]);
    if (!userSnap.exists()) return;
    const updates = publicProfileUpdates(
      userSnap.data() as Record<string, any>,
      pageSnap.exists() ? (pageSnap.data() as Record<string, any>) : {}
    );
    if (Object.keys(updates).length === 0) return;
    await setDoc(doc(db, 'missionpages', userId), { ...updates, updatedAt: serverTimestamp() }, { merge: true });
  } catch (error) {
    console.warn('[publishMissionPage] could not mirror public profile', error);
  }
}

export async function publishMissionPage(userId: string, publish: boolean = true) {
  try {
    const pageRef = doc(db, 'missionpages', userId);
    if (publish) {
      const existing = await getDoc(pageRef);
      if (!existing.exists() || !existing.data()?.isPublished) {
        trackPagePublished();
      }
    }
    await setDoc(pageRef, {
      isPublished: publish,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    // Going live is the moment the public doc has to be able to stand on its own.
    if (publish) await mirrorPublicProfile(userId);
  } catch (error: any) {
    console.error('Error publishing fund page:', error);
    throw new Error(error.message || 'Failed to publish fund page');
  }
}

/**
 * Mirror "this creator has somewhere to send the money" onto the publicly
 * readable page doc (TSF-176). Owner-only write, best-effort: a failure here
 * must never take down a publish or a dashboard load.
 */
export async function setGivingEnabled(userId: string, enabled: boolean) {
  try {
    const ref = doc(db, 'missionpages', userId);

    // Write ONLY when the value actually changes.
    //
    // The dashboard fires this on every load. A pending Firestore write makes
    // the SDK serve its own local mutation as the document to any CONCURRENT
    // read -- and before the server copy is cached, that local view is just the
    // written fields. getMissionPage(), running alongside, therefore received
    // { givingEnabled, updatedAt } with no isPublished, and the dashboard told
    // a creator whose page was live "your page is not published -- nobody can
    // see it or give to it yet". She re-published by hand on every visit and
    // wrote in to report it.
    //
    // Proven by disabling this one call and re-reading: the same read went from
    //   keys=givingEnabled,updatedAt        isPublished=undefined
    // to
    //   keys=title,isPublished,givingEnabled,updatedAt   isPublished=true
    //
    // getDocFromServer() does NOT help -- pending local mutations are overlaid
    // on that result too. Not issuing the no-op write is the fix, and it also
    // stops a pointless write on every dashboard load.
    const existing = await getDoc(ref);
    if (existing.exists() && existing.data()?.givingEnabled === enabled) return;

    await setDoc(ref, {
      givingEnabled: enabled,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.warn('[setGivingEnabled] could not mirror giving status', error);
  }
}

/**
 * Mirror a single public-facing profile field onto the publicly readable
 * missionpages doc (TSF-238: profile photo and biography were saved only to
 * users/{uid}, which a logged-out donor cannot read -- see the security fix
 * comment on that rule -- so they never reached the donor even when set).
 * merge:true so this can never clobber headerImage/title/description/
 * isPublished the way saveMissionPage's full-overwrite pattern would.
 */
export async function setPublicProfileFields(
  userId: string,
  fields: Partial<Pick<MissionPage, 'photoURL' | 'description' | 'headerImage'>>
) {
  try {
    await setDoc(doc(db, 'missionpages', userId), {
      ...fields,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.warn('[setPublicProfileFields] could not mirror public profile field', error);
  }
}

export const getMissionBySlug = async (slug: string): Promise<Mission | null> => {
  console.log(`[getMissionBySlug] Fetching fund page with slug: ${slug}`);

  try {
    const missionRef = collection(db, 'missions');
    const q = query(missionRef, where('slug', '==', slug));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log(`[getMissionBySlug] No fund page found for slug: ${slug}`);
      return null;
    }

    const missionData = querySnapshot.docs[0].data() as Mission;
    console.log('[getMissionBySlug] Successfully fetched fund page:', missionData);
    return missionData;
  } catch (error) {
    console.error('[getMissionBySlug] Error fetching fund page:', error);
    throw error;
  }
};

export { publicProfileUpdates } from './publicProfileFields';

export { definedOnly } from './definedOnly';
